import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent 
} from '@/components/ui/chart';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  AreaChart, 
  Area,
  CartesianGrid, 
  XAxis, 
  YAxis 
} from 'recharts';
import { Trophy, Flame, Target, Clock, TrendingUp } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface PlayerStatisticsPanelProps {
  userId: string;
  currentElo: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  gamesDrawn: number;
}

interface GameHistoryItem {
  id: string;
  white_player_id: string | null;
  black_player_id: string | null;
  winner: string | null;
  time_control: string | null;
  created_at: string;
  game_state: any;
  move_count: number;
}

interface EloHistoryItem {
  date: string;
  elo: number;
}

export function PlayerStatisticsPanel({
  userId,
  currentElo,
  gamesPlayed,
  gamesWon,
  gamesLost,
  gamesDrawn,
}: PlayerStatisticsPanelProps) {
  const [timeRange, setTimeRange] = useState<string>('all');
  const [games, setGames] = useState<GameHistoryItem[]>([]);
  const [eloHistory, setEloHistory] = useState<EloHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('game_sessions')
          .select('id, white_player_id, black_player_id, winner, time_control, created_at, game_state, move_count')
          .or(`white_player_id.eq.${userId},black_player_id.eq.${userId}`)
          .order('created_at', { ascending: false })
          .limit(200);

        if (!error && data) {
          setGames(data as GameHistoryItem[]);
        }
      } catch (err) {
        console.error('Error fetching games:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [userId]);

  useEffect(() => {
    const fetchEloHistory = async () => {
      const { data, error } = await supabase
        .from('elo_change_logs')
        .select('created_at, white_player_id, black_player_id, white_elo_after, black_elo_after')
        .or(`white_player_id.eq.${userId},black_player_id.eq.${userId}`)
        .order('created_at', { ascending: true });
      
      if (!error && data) {
        const eloData = data.map(log => ({
          date: log.created_at,
          elo: log.white_player_id === userId ? log.white_elo_after : log.black_elo_after
        }));
        setEloHistory(eloData);
      }
    };
    
    fetchEloHistory();
  }, [userId]);

  const filteredGames = useMemo(() => {
    if (timeRange === 'all') return games;
    
    const days = parseInt(timeRange);
    const cutoffDate = subDays(new Date(), days);
    
    return games.filter(game => new Date(game.created_at) >= cutoffDate);
  }, [games, timeRange]);

  const getGameResult = (game: GameHistoryItem): 'win' | 'loss' | 'draw' => {
    if (!game.winner || game.winner === 'draw') return 'draw';
    
    const playerColor = game.white_player_id === userId ? 'white' : 'black';
    return game.winner === playerColor ? 'win' : 'loss';
  };

  const recentGames = useMemo(() => {
    return filteredGames.slice(0, 10).map(getGameResult);
  }, [filteredGames]);

  const streakText = useMemo(() => {
    if (recentGames.length === 0) return 'No games played';
    
    let currentStreak = 1;
    const firstResult = recentGames[0];
    
    for (let i = 1; i < recentGames.length; i++) {
      if (recentGames[i] === firstResult) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    if (firstResult === 'win') {
      return `${currentStreak}-game winning streak`;
    } else if (firstResult === 'loss') {
      return `${currentStreak} ${currentStreak === 1 ? 'loss' : 'losses'} in a row`;
    } else {
      return `${currentStreak} ${currentStreak === 1 ? 'draw' : 'draws'} in a row`;
    }
  }, [recentGames]);

  const performanceData = useMemo(() => {
    const gamesByPeriod = new Map<string, { wins: number; total: number }>();
    
    filteredGames.forEach(game => {
      const period = format(new Date(game.created_at), 'yyyy-MM-dd');
      const result = getGameResult(game);
      
      if (!gamesByPeriod.has(period)) {
        gamesByPeriod.set(period, { wins: 0, total: 0 });
      }
      
      const stats = gamesByPeriod.get(period)!;
      stats.total += 1;
      if (result === 'win') stats.wins += 1;
    });
    
    return Array.from(gamesByPeriod.entries())
      .map(([date, stats]) => ({
        date,
        winRate: Math.round((stats.wins / stats.total) * 100),
        games: stats.total
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredGames]);

  const timeControlData = useMemo(() => {
    const tcMap = new Map<string, { total: number; wins: number; losses: number; draws: number }>();
    
    filteredGames.forEach(game => {
      const tc = game.time_control || 'Unknown';
      if (!tcMap.has(tc)) {
        tcMap.set(tc, { total: 0, wins: 0, losses: 0, draws: 0 });
      }
      
      const stats = tcMap.get(tc)!;
      stats.total += 1;
      
      const result = getGameResult(game);
      if (result === 'win') stats.wins += 1;
      else if (result === 'loss') stats.losses += 1;
      else stats.draws += 1;
    });
    
    return Array.from(tcMap.entries())
      .map(([timeControl, stats]) => ({
        timeControl,
        ...stats,
        winRate: Math.round((stats.wins / stats.total) * 100)
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredGames]);

  const mostSuccessfulTimeControl = useMemo(() => {
    if (timeControlData.length === 0) return null;
    return timeControlData.reduce((best, current) => 
      current.winRate > best.winRate && current.total >= 3 ? current : best
    );
  }, [timeControlData]);

  const longestStreak = useMemo(() => {
    let maxStreak = 0;
    let currentStreak = 0;
    
    games.forEach(game => {
      const result = getGameResult(game);
      
      if (result === 'win') {
        currentStreak++;
        if (currentStreak > maxStreak) {
          maxStreak = currentStreak;
        }
      } else {
        currentStreak = 0;
      }
    });
    
    return maxStreak;
  }, [games]);

  const filteredEloHistory = useMemo(() => {
    if (timeRange === 'all') return eloHistory;
    
    const days = parseInt(timeRange);
    const cutoffDate = subDays(new Date(), days);
    
    return eloHistory.filter(item => new Date(item.date) >= cutoffDate);
  }, [eloHistory, timeRange]);

  const eloStats = useMemo(() => {
    if (filteredEloHistory.length === 0) {
      return { peak: currentElo, gain: 0 };
    }
    
    const peak = Math.max(...filteredEloHistory.map(e => e.elo));
    const gain = currentElo - filteredEloHistory[0].elo;
    
    return { peak, gain };
  }, [filteredEloHistory, currentElo]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (filteredGames.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detailed Statistics</CardTitle>
          <CardDescription>Performance analysis and trends</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No games in this time period</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Detailed Statistics</CardTitle>
            <CardDescription>Performance analysis and trends</CardDescription>
          </div>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <Clock className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 3 Months</SelectItem>
              <SelectItem value="180">Last 6 Months</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="time-controls">Time Controls</TabsTrigger>
            <TabsTrigger value="elo">ELO History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-6">
            {/* Recent Form */}
            <div>
              <h3 className="text-sm font-medium mb-3">Recent Form (Last 10 Games)</h3>
              <div className="flex flex-wrap gap-2">
                {recentGames.map((result, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      "w-10 h-10 rounded flex items-center justify-center font-bold",
                      result === 'win' && "bg-green-500 text-white",
                      result === 'loss' && "bg-red-500 text-white",
                      result === 'draw' && "bg-gray-400 text-white"
                    )}
                  >
                    {result === 'win' ? 'W' : result === 'loss' ? 'L' : 'D'}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-2">{streakText}</p>
            </div>
            
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-8 h-8 text-yellow-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Best Time Control</p>
                      <p className="text-lg font-bold">
                        {mostSuccessfulTimeControl?.timeControl || 'N/A'}
                      </p>
                      {mostSuccessfulTimeControl && (
                        <p className="text-sm text-muted-foreground">
                          {mostSuccessfulTimeControl.winRate}% win rate
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Flame className="w-8 h-8 text-orange-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Longest Streak</p>
                      <p className="text-lg font-bold">
                        {longestStreak} {longestStreak === 1 ? 'game' : 'games'}
                      </p>
                      <p className="text-sm text-muted-foreground">Winning streak</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Target className="w-8 h-8 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Games Analyzed</p>
                      <p className="text-lg font-bold">{filteredGames.length}</p>
                      <p className="text-sm text-muted-foreground">In selected period</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="performance">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Win Rate Over Time</h3>
              {performanceData.length > 0 ? (
                <ChartContainer
                  config={{
                    winRate: {
                      label: "Win Rate %",
                      color: "hsl(var(--primary))",
                    },
                  }}
                  className="h-[200px] md:h-[300px]"
                >
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                    />
                    <YAxis domain={[0, 100]} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line 
                      type="monotone" 
                      dataKey="winRate" 
                      stroke="var(--color-winRate)" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ChartContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Not enough data to show performance trends</p>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="time-controls">
            <div className="space-y-6">
              {/* Distribution Bar Chart */}
              <div>
                <h3 className="text-sm font-medium mb-3">Games Played by Time Control</h3>
                {timeControlData.length > 0 ? (
                  <ChartContainer
                    config={{
                      total: { label: "Games", color: "hsl(var(--primary))" }
                    }}
                    className="h-[200px] md:h-[250px]"
                  >
                    <BarChart data={timeControlData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timeControl" />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="total" fill="var(--color-total)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>No time control data available</p>
                  </div>
                )}
              </div>
              
              {/* Win Rate by Time Control */}
              {timeControlData.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-3">Performance by Time Control</h3>
                  <div className="space-y-3">
                    {timeControlData.map(tc => (
                      <div key={tc.timeControl} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <div className="w-full sm:w-24 font-medium">{tc.timeControl}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Progress value={tc.winRate} className="flex-1" />
                            <span className="text-sm text-muted-foreground w-12">
                              {tc.winRate}%
                            </span>
                          </div>
                          <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                            <span className="text-green-600">{tc.wins}W</span>
                            <span className="text-red-600">{tc.losses}L</span>
                            <span className="text-gray-500">{tc.draws}D</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="elo">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h3 className="text-sm font-medium">ELO Rating Progression</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Current: </span>
                    <span className="font-bold">{currentElo}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Peak: </span>
                    <span className="font-bold">{eloStats.peak}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Change: </span>
                    <span className={cn(
                      "font-bold",
                      eloStats.gain > 0 ? "text-green-600" : eloStats.gain < 0 ? "text-red-600" : ""
                    )}>
                      {eloStats.gain > 0 ? '+' : ''}
                      {eloStats.gain}
                    </span>
                  </div>
                </div>
              </div>
              
              {filteredEloHistory.length > 0 ? (
                <ChartContainer
                  config={{
                    elo: {
                      label: "ELO Rating",
                      color: "hsl(var(--primary))",
                    },
                  }}
                  className="h-[200px] md:h-[300px]"
                >
                  <AreaChart data={filteredEloHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                    />
                    <YAxis domain={['dataMin - 50', 'dataMax + 50']} />
                    <ChartTooltip 
                      content={<ChartTooltipContent />}
                      labelFormatter={(value) => format(new Date(value as string), 'PPP')}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="elo" 
                      stroke="var(--color-elo)" 
                      fill="var(--color-elo)"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ChartContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No ELO history available for this period</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
